/**
 * スタンプ/アイコン定義
 * FreehandCanvas と Draw-popup で共有
 */

import { escapeHtml } from '@/js/utils/sanitize'

// @mdi/js は2.8MBあるため遅延ロード（起動高速化）
let _mdiIcons: Record<string, string> | null = null
async function loadMdiIcons(): Promise<Record<string, string>> {
  if (!_mdiIcons) {
    _mdiIcons = await import('@mdi/js') as unknown as Record<string, string>
  }
  return _mdiIcons
}
// 同期アクセス用（ロード済みの場合のみ）
function getMdiIconsSync(): Record<string, string> | null {
  return _mdiIcons
}

export interface Stamp {
  id: string
  label: string
  color: string
  text?: string  // 数字スタンプ用
  path?: string  // SVGパス（通常スタンプ用）
  mdiIcon?: string  // MDIアイコン名（例: 'mdi-star'）
}

/**
 * MDIアイコンID（例: 'mdi-star'）を@mdi/jsのエクスポート名（例: 'mdiStar'）に変換
 */
export function mdiIdToExportName(mdiId: string): string {
  // 'mdi-star' → 'mdiStar'
  // 'mdi-access-point' → 'mdiAccessPoint'
  if (!mdiId.startsWith('mdi-')) {
    return mdiId
  }
  const parts = mdiId.split('-')
  // 最初の 'mdi' はそのまま、残りをcamelCaseに
  return parts.map((part, index) => {
    if (index === 0) return part
    return part.charAt(0).toUpperCase() + part.slice(1)
  }).join('')
}

/**
 * MDIアイコンIDからSVGパスを取得（同期版 — アイコン未ロード時はundefined）
 * @param mdiId MDIアイコンID（例: 'mdi-star', 'mdiStar'）
 * @returns SVGパス文字列、見つからなければundefined
 */
export function getMdiIconPath(mdiId: string): string | undefined {
  const icons = getMdiIconsSync()
  if (!icons) return undefined
  // すでにcamelCaseの場合（mdiStar）
  if (mdiId in icons) {
    return icons[mdiId]
  }
  // kebab-caseの場合（mdi-star）→ camelCaseに変換
  const exportName = mdiIdToExportName(mdiId)
  if (exportName in icons) {
    return icons[exportName]
  }
  return undefined
}

/**
 * MDIアイコンIDからSVGパスを取得（非同期版 — 必要時に自動ロード）
 */
export async function getMdiIconPathAsync(mdiId: string): Promise<string | undefined> {
  const icons = await loadMdiIcons()
  if (mdiId in icons) {
    return icons[mdiId]
  }
  const exportName = mdiIdToExportName(mdiId)
  if (exportName in icons) {
    return icons[exportName]
  }
  return undefined
}

/**
 * 全MDIアイコン名を取得（検索用 — 非同期）
 */
export async function getAllMdiIconNames(): Promise<string[]> {
  const icons = await loadMdiIcons()
  return Object.keys(icons).filter(key => key.startsWith('mdi'))
}

/**
 * MDIアイコンをプリロード（地図表示後に呼ぶ）
 */
export function preloadMdiIcons(): void {
  loadMdiIcons()
}

export interface StampCategory {
  id: string
  name: string
  stamps: Stamp[]
}

export const STAMP_CATEGORIES: StampCategory[] = [
  {
    id: 'numbers',
    name: '数字',
    stamps: [
      { id: 'num-1', label: '①', color: '#0066CC', text: '1' },
      { id: 'num-2', label: '②', color: '#0066CC', text: '2' },
      { id: 'num-3', label: '③', color: '#0066CC', text: '3' },
      { id: 'num-4', label: '④', color: '#0066CC', text: '4' },
      { id: 'num-5', label: '⑤', color: '#0066CC', text: '5' },
      { id: 'num-6', label: '⑥', color: '#0066CC', text: '6' },
      { id: 'num-7', label: '⑦', color: '#0066CC', text: '7' },
      { id: 'num-8', label: '⑧', color: '#0066CC', text: '8' },
      { id: 'num-9', label: '⑨', color: '#0066CC', text: '9' },
      { id: 'num-10', label: '⑩', color: '#0066CC', text: '10' },
    ]
  },
  {
    id: 'arrows',
    name: '矢印',
    stamps: [
      { id: 'arrow-up', label: '↑', color: '#FF0000', path: 'M12,4L4,12H8V20H16V12H20L12,4Z' },
      { id: 'arrow-down', label: '↓', color: '#FF0000', path: 'M12,20L20,12H16V4H8V12H4L12,20Z' },
      { id: 'arrow-left', label: '←', color: '#FF0000', path: 'M4,12L12,20V16H20V8H12V4L4,12Z' },
      { id: 'arrow-right', label: '→', color: '#FF0000', path: 'M20,12L12,4V8H4V16H12V20L20,12Z' },
      { id: 'arrow-up-right', label: '↗', color: '#FF0000', path: 'M5,17.59L15.59,7H9V5H19V15H17V8.41L6.41,19L5,17.59Z' },
      { id: 'arrow-down-right', label: '↘', color: '#FF0000', path: 'M5,6.41L15.59,17H9V19H19V9H17V15.59L6.41,5L5,6.41Z' },
      { id: 'arrow-down-left', label: '↙', color: '#FF0000', path: 'M19,6.41L8.41,17H15V19H5V9H7V15.59L17.59,5L19,6.41Z' },
      { id: 'arrow-up-left', label: '↖', color: '#FF0000', path: 'M19,17.59L8.41,7H15V5H5V15H7V8.41L17.59,19L19,17.59Z' },
    ]
  },
  {
    id: 'marks',
    name: '記号',
    stamps: [
      { id: 'mark-circle', label: '○', color: '#0000FF', path: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4Z' },
      { id: 'mark-circle-fill', label: '●', color: '#0000FF', path: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' },
      { id: 'mark-cross', label: '×', color: '#FF0000', path: 'M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z' },
      { id: 'mark-check', label: '✓', color: '#008000', path: 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' },
      { id: 'mark-triangle', label: '△', color: '#FF8C00', path: 'M12,2L1,21H23L12,2M12,6L19.53,19H4.47L12,6Z' },
      { id: 'mark-triangle-fill', label: '▲', color: '#FF8C00', path: 'M12,2L1,21H23L12,2Z' },
      { id: 'mark-square', label: '□', color: '#607D8B', path: 'M3,3V21H21V3H3M5,5H19V19H5V5Z' },
      { id: 'mark-square-fill', label: '■', color: '#607D8B', path: 'M3,3V21H21V3H3Z' },
      { id: 'mark-diamond', label: '◇', color: '#009688', path: 'M12,2L2,12L12,22L22,12L12,2M12,5L19,12L12,19L5,12L12,5Z' },
      { id: 'mark-diamond-fill', label: '◆', color: '#009688', path: 'M12,2L2,12L12,22L22,12L12,2Z' },
      { id: 'mark-star', label: '☆', color: '#FFC107', path: 'M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.63L12,2L9.19,8.63L2,9.24L7.46,13.97L5.82,21L12,17.27M12,5.67L13.93,10.24L18.93,10.65L15.17,13.81L16.37,18.71L12,16L7.63,18.71L8.83,13.81L5.07,10.65L10.07,10.24L12,5.67Z' },
      { id: 'mark-star-fill', label: '★', color: '#FFC107', path: 'M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.63L12,2L9.19,8.63L2,9.24L7.46,13.97L5.82,21L12,17.27Z' },
    ]
  },
  {
    id: 'warning',
    name: '警告',
    stamps: [
      { id: 'warn-alert', label: '⚠', color: '#FF9800', path: 'M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z' },
      { id: 'warn-danger', label: '危', color: '#F44336', path: 'M12,2L1,21H23L12,2M12,6L19.53,19H4.47L12,6M11,10V14H13V10H11M11,16V18H13V16H11Z' },
      { id: 'warn-info', label: 'ℹ', color: '#2196F3', path: 'M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' },
      { id: 'warn-stop', label: '⛔', color: '#D32F2F', path: 'M12,2C17.5,2 22,6.5 22,12C22,17.5 17.5,22 12,22C6.5,22 2,17.5 2,12C2,6.5 6.5,2 12,2M12,4C7.58,4 4,7.58 4,12C4,14.21 4.89,16.21 6.34,17.66L17.66,6.34C16.21,4.89 14.21,4 12,4M12,20C16.42,20 20,16.42 20,12C20,9.79 19.11,7.79 17.66,6.34L6.34,17.66C7.79,19.11 9.79,20 12,20Z' },
      { id: 'warn-construction', label: '🚧', color: '#FF5722', path: 'M13.5,5.5C14.59,5.5 15.5,4.58 15.5,3.5C15.5,2.38 14.59,1.5 13.5,1.5C12.39,1.5 11.5,2.38 11.5,3.5C11.5,4.58 12.39,5.5 13.5,5.5M9.89,19.38L10.89,15L13,17V23H15V15.5L12.89,13.5L13.5,10.5C14.79,12 16.79,13 19,13V11C17.09,11 15.5,10 14.69,8.58L13.69,7C13.29,6.38 12.69,6 12,6C11.69,6 11.5,6.08 11.19,6.08L6,8.28V13H8V9.58L9.79,8.88L8.19,17L3.29,16L2.89,18L9.89,19.38Z' },
    ]
  },
  {
    id: 'facility',
    name: '施設',
    stamps: [
      { id: 'fac-home', label: '🏠', color: '#795548', path: 'M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z' },
      { id: 'fac-building', label: '🏢', color: '#607D8B', path: 'M5,3V21H11V17.5H13V21H19V3H5M7,5H9V7H7V5M11,5H13V7H11V5M15,5H17V7H15V5M7,9H9V11H7V9M11,9H13V11H11V9M15,9H17V11H15V9M7,13H9V15H7V13M11,13H13V15H11V13M15,13H17V15H15V13Z' },
      { id: 'fac-hospital', label: '🏥', color: '#E91E63', path: 'M18,14H14V18H10V14H6V10H10V6H14V10H18V14M19,3H5C3.89,3 3,3.89 3,5V19C3,20.11 3.89,21 5,21H19C20.11,21 21,20.11 21,19V5C21,3.89 20.11,3 19,3Z' },
      { id: 'fac-school', label: '🏫', color: '#3F51B5', path: 'M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z' },
      { id: 'fac-parking', label: 'P', color: '#1976D2', path: 'M13,3H6V21H10V15H13A4,4 0 0,0 17,11V7A4,4 0 0,0 13,3M13,11H10V7H13A1,1 0 0,1 14,8V10A1,1 0 0,1 13,11Z' },
      { id: 'fac-train', label: '🚃', color: '#673AB7', path: 'M12,2C8,2 4,2.5 4,6V15.5C4,17.43 5.57,19 7.5,19L6,20.5V21H8.23L10.23,19H14L16,21H18V20.5L16.5,19C18.43,19 20,17.43 20,15.5V6C20,2.5 16.42,2 12,2M7.5,17C6.67,17 6,16.33 6,15.5C6,14.67 6.67,14 7.5,14C8.33,14 9,14.67 9,15.5C9,16.33 8.33,17 7.5,17M11,10H6V6H11V10M13,10V6H18V10H13M16.5,17C15.67,17 15,16.33 15,15.5C15,14.67 15.67,14 16.5,14C17.33,14 18,14.67 18,15.5C18,16.33 17.33,17 16.5,17Z' },
      { id: 'fac-bus', label: '🚌', color: '#00BCD4', path: 'M18,11H6V6H18M16.5,17A1.5,1.5 0 0,1 15,15.5A1.5,1.5 0 0,1 16.5,14A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 16.5,17M7.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,14A1.5,1.5 0 0,1 9,15.5A1.5,1.5 0 0,1 7.5,17M4,16C4,16.88 4.39,17.67 5,18.22V20A1,1 0 0,0 6,21H7A1,1 0 0,0 8,20V19H16V20A1,1 0 0,0 17,21H18A1,1 0 0,0 19,20V18.22C19.61,17.67 20,16.88 20,16V6C20,2.5 16.42,2 12,2C7.58,2 4,2.5 4,6V16Z' },
    ]
  },
  {
    id: 'colors',
    name: 'カラー',
    stamps: [
      { id: 'pin-red', label: '📍', color: '#F44336', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-blue', label: '📍', color: '#2196F3', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-green', label: '📍', color: '#4CAF50', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-orange', label: '📍', color: '#FF9800', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-purple', label: '📍', color: '#9C27B0', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-yellow', label: '📍', color: '#FFEB3B', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-pink', label: '📍', color: '#E91E63', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
      { id: 'pin-black', label: '📍', color: '#212121', path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z' },
    ]
  },
  {
    id: 'misc',
    name: 'その他',
    stamps: [
      { id: 'misc-flag', label: '🚩', color: '#F44336', path: 'M14.4,6L14,4H5V21H7V14H12.6L13,16H20V6H14.4Z' },
      { id: 'misc-camera', label: '📷', color: '#607D8B', path: 'M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z' },
      { id: 'misc-eye', label: '👁', color: '#9C27B0', path: 'M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z' },
      { id: 'misc-heart', label: '❤', color: '#E91E63', path: 'M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z' },
      { id: 'misc-target', label: '◎', color: '#3F51B5', path: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10Z' },
      { id: 'misc-lightbulb', label: '💡', color: '#FFC107', path: 'M12,2A7,7 0 0,0 5,9C5,11.38 6.19,13.47 8,14.74V17A1,1 0 0,0 9,18H15A1,1 0 0,0 16,17V14.74C17.81,13.47 19,11.38 19,9A7,7 0 0,0 12,2M9,21A1,1 0 0,0 10,22H14A1,1 0 0,0 15,21V20H9V21Z' },
    ]
  },
]

/**
 * 全スタンプをフラット配列で取得
 */
export function getAllStamps(): Stamp[] {
  return STAMP_CATEGORIES.flatMap(c => c.stamps)
}

/**
 * IDからスタンプを検索
 */
export function findStampById(stampId: string): Stamp | undefined {
  return getAllStamps().find(s => s.id === stampId)
}

/**
 * スタンプのHTML要素を生成（Marker用）
 * @param stamp スタンプ情報
 * @param colorOverride 色のオーバーライド（指定された場合はstamp.colorの代わりに使用）
 */
export function createStampElement(stamp: Stamp, colorOverride?: string): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'stamp-marker'
  el.style.cssText = 'cursor: pointer;'

  // 色: オーバーライドがあればそれを使用、なければstamp.color
  const color = colorOverride || stamp.color

  if (stamp.text) {
    // 数字スタンプ（ユーザー入力をエスケープ）
    el.innerHTML = `<div class="stamp-number-marker" style="color: ${escapeHtml(color)}; border-color: ${escapeHtml(color)};">${escapeHtml(stamp.text)}</div>`
  } else {
    // SVGパスを取得（mdiIcon優先、次にid、最後にpath）
    let svgPath = stamp.path
    if (stamp.mdiIcon) {
      const mdiPath = getMdiIconPath(stamp.mdiIcon)
      if (mdiPath) {
        svgPath = mdiPath
      }
    } else if (stamp.id) {
      // stamp.idがMDIアイコン名の場合も解決を試みる
      const mdiPath = getMdiIconPath(stamp.id)
      if (mdiPath) {
        svgPath = mdiPath
      }
    }
    if (svgPath) {
      el.innerHTML = `
        <svg width="32" height="32" viewBox="0 0 24 24">
          <path fill="${color}" d="${svgPath}" />
        </svg>
      `
    }
  }

  return el
}

/**
 * stampIdまたはmdiIconからスタンプ要素を生成
 * GeoJSONのプロパティから直接生成する用
 * @param stampId スタンプID（例: 'mark-star', 'mdi-star'）
 * @param color 色
 * @param stampText 数字テキスト（数字スタンプ用）
 */
export function createStampElementFromId(
  stampId: string,
  color: string,
  stampText?: string
): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'stamp-marker'
  el.style.cssText = 'cursor: pointer;'

  if (stampText) {
    // 数字スタンプ（ユーザー入力をエスケープ）
    el.innerHTML = `<div class="stamp-number-marker" style="color: ${escapeHtml(color)}; border-color: ${escapeHtml(color)};">${escapeHtml(stampText)}</div>`
  } else {
    // まずMDIアイコンとして解決を試みる
    let svgPath = getMdiIconPath(stampId)

    // MDIで見つからなければ既存のスタンプ定義から探す
    if (!svgPath) {
      const stamp = findStampById(stampId)
      if (stamp?.path) {
        svgPath = stamp.path
      } else if (stamp?.mdiIcon) {
        svgPath = getMdiIconPath(stamp.mdiIcon)
      }
    }

    if (svgPath) {
      el.innerHTML = `
        <svg width="32" height="32" viewBox="0 0 24 24">
          <path fill="${color}" d="${svgPath}" />
        </svg>
      `
    }
  }

  return el
}
